<template>
  <div>
    <b-row>
      <!-- Affichage de la video actuel ou nouvellement selectionné -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 8 : 12">
        <div
          class="video-container"
          :class="{
            empty: !hasSelectedVideo,
            'cursor-pointer': !hasSelectedVideo && emptyContainerAction !== 'none',
          }"
          @click="handleEmptyContainerClick">
          <b-overlay :show="uploading">
            <div v-if="hasSelectedVideo" class="video-player">
              <video controls :src="selectedVideo.path" class="rounded w-100" preload="none"></video>
              <p>{{ selectedVideo.name }}</p>
            </div>

            <div v-else class="placeholder">
              <span>Aucune vidéo sélectionnée</span>
            </div>
          </b-overlay>
        </div>
      </b-col>

      <!-- Boutons d'actions -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 4 : 12" class="mt-2 mt-md-0">
        <div class="button-container">
          <b-button
            v-if="!importButtonDisabled"
            class="w-100 text-nowrap"
            @click="openFileInput"
            :disabled="disabled || uploading">
            Importer depuis mon ordinateur
          </b-button>
          <b-button
            v-if="!chooseButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            @click="openLibraryModal"
            :disabled="disabled || uploading">
            Choisir dans la bibliothèque
          </b-button>
          <b-button
            v-if="hasSelectedVideo && !resetButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            variant="danger"
            @click="resetVideo"
            :disabled="disabled || uploading">
            Réinitialiser
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Input pour l'import de fichier -->
    <input type="file" ref="fileInput" style="display: none" @change="onFileSelected" accept="video/*" />

    <!-- Modal pour la bibliothèque d'videos -->
    <b-modal ref="libraryModal" title="Choisir une vidéo" size="xl" centered hide-footer>
      <b-row class="mb-4">
        <b-col cols="12">
          <b-form-group id="fieldset-search-uploads-1" label="Rechercher" label-for="search-uploads">
            <b-form-input id="search-uploads" v-model="search" trim></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- loading -->
      <div v-if="isLoading">
        <b-spinner />
      </div>

      <!-- empty container -->
      <div v-if="list.items.length === 0 && !isLoading">
        <p class="text-center">Aucun fichier trouvé</p>
      </div>

      <b-row align-v="stretch" v-else-if="list.items.length > 0">
        <b-col
          v-for="(file, index) in list.items"
          :key="index"
          cols="12"
          :sm="6"
          :md="4"
          :lg="3"
          class="mb-2 cursor-pointer"
          @click="selectLibraryFile(file)"
          v-b-tooltip.hover.bottom
          :title="file.name">
          <div class="video-wrapper" @click.stop>
            <video
              controls
              preload="none"
              :ref="'video__uploads__' + index"
              :src="file.path"
              class="rounded w-100"></video>
          </div>
          <div class="gallery__video-name-banner font-weight-light font-size-xs">
            {{ file.name }}
          </div>
        </b-col>
      </b-row>

      <!-- pagination -->
      <div v-if="list.meta.total > 0" class="pagination-bar mb-3">
        <div class="pagination-total">{{ list.meta.total }} élément(s)</div>
        <div class="pagination-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="list.meta.total"
            :per-page="perPage"
            align="center"
            @page-click="(event, page) => fetchFilesLibrary(page)"
            class="m-0"></b-pagination>
        </div>
        <div class="pagination-select">
          <b-form-select
            v-model="perPage"
            :options="[
              { value: 8, text: '8 par page' },
              { value: 16, text: '16 par page' },
              { value: 24, text: '24 par page' },
              { value: 32, text: '32 par page' },
            ]"
            size="sm"
            style="width: 120px"
            @change="handlePerPageChange"></b-form-select>
        </div>
      </div>
    </b-modal>

    <video ref="videoPlayer" class="d-none"></video>
  </div>
</template>

<script>
  import axios from 'axios'
  import _isEqual from 'lodash/isEqual'
  import _debounce from 'lodash/debounce'
  import fileService from '@/services/fileService'

  export default {
    name: 'VideoSelector',
    components: {},
    props: {
      buttonsPosition: {
        type: String,
        default: 'bottom',
        validator: (value) => ['bottom', 'right'].includes(value),
      },
      initialVideo: {
        type: Object,
        default: null,
      },
      emptyContainerAction: {
        type: String,
        default: 'none',
        validator: (value) => ['none', 'import', 'choose'].includes(value),
      },
      uploadFileCategory: {
        type: String,
        default: 'default',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      importButtonDisabled: {
        type: Boolean,
        default: false,
      },
      chooseButtonDisabled: {
        type: Boolean,
        default: false,
      },
      resetButtonDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        selectedVideo: this.initialVideo,
        showOverlay: false,
        uploading: false,
        search: '',
        playingVideo: null,
        debouncedSearch: null,
        isLoading: false,
        currentPage: 1,
        perPage: 8,
        list: {
          items: [],
          meta: {
            total: 0,
            page: this.currentPage,
            perPage: this.perPage,
            totalPages: 0,
            prevPage: null,
            nextPage: null,
          },
        },
      }
    },
    created() {
      this.debouncedSearch = _debounce(this.handleSearch, 500)
    },
    watch: {
      search(newValue) {
        this.debouncedSearch(newValue)
      },
      initialVideo(newValue, oldValue) {
        if (_isEqual(newValue, oldValue)) {
          return
        }

        this.selectedVideo = newValue
      },
    },
    computed: {
      jwt() {
        return localStorage.getItem('jwt')
      },
      hasSelectedVideo() {
        return !!this.selectedVideo && !!this.selectedVideo.name
      },
    },
    methods: {
      async handleSearch() {
        await this.fetchFilesLibrary(1)
      },
      async handlePerPageChange() {
        this.currentPage = 1
        await this.fetchFilesLibrary(1)
      },
      async fetchFilesLibrary(newPage) {
        this.isLoading = true
        this.list = await fileService.paginate({
          page: newPage,
          limit: this.perPage,
          type: 'video',
          search: this.search,
        })
        this.isLoading = false
      },
      isPlaying(video) {
        return this.playingVideo && this.playingVideo.name === video.name && this.playingVideo.path === video.path
      },
      playVideo(video) {
        // stop video if already playing
        if (this.playingVideo) {
          this.$refs.videoPlayer.pause()
        }

        this.$refs.videoPlayer.src = video.path
        this.$refs.videoPlayer.play()
        this.playingVideo = video
      },
      stopVideo() {
        this.$refs.videoPlayer.pause()
        this.playingVideo = null
      },

      handleEmptyContainerClick() {
        if (this.uploading) {
          return
        }

        if (this.disabled) {
          return
        }

        if (!this.hasSelectedVideo) {
          if (this.emptyContainerAction === 'import') {
            this.openFileInput()
          } else if (this.emptyContainerAction === 'choose') {
            this.openLibraryModal()
          }
        }
      },
      openFileInput() {
        this.$refs.fileInput.click()
      },
      async openLibraryModal() {
        this.isLoading = true
        await this.fetchFilesLibrary(this.currentPage)
        this.isLoading = false
        this.$refs.libraryModal.show()
      },
      async onFileSelected(event) {
        const file = event.target.files[0]
        if (!file) {
          return
        }

        this.uploading = true
        try {
          const uploadedVideoResponse = await this.uploadFile(file)
          const uploadedVideo = uploadedVideoResponse
          const uploadedVideoData = {
            name: uploadedVideo.name,
            path: uploadedVideo.path,
            url: uploadedVideo.url,
          }
          this.selectedVideo = uploadedVideoData
          this.$emit('file-uploaded', uploadedVideoData)
          this.$emit('file-selected', uploadedVideoData)
          this.$notify({
            type: 'success',
            title: 'Fichier téléchargé',
            text: 'Le fichier a été téléchargé avec succès',
          })
        } catch (e) {
          console.error(e)
          this.$notify({
            type: 'error',
            title: e || 'Erreur lors du téléchargement',
            text: e || 'Une erreur est survenue lors du téléchargement du fichier',
          })
        } finally {
          this.uploading = false
        }
      },
      async uploadFile(file) {
        if (!file) {
          return
        }

        const formData = new FormData()
        formData.append('category', this.uploadFileCategory)
        formData.append('file', file)

        const response = await axios.post('/api/files', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + this.jwt,
          },
        })

        return response.data
      },
      selectLibraryFile(video) {
        this.selectedVideo = video
        this.$emit('file-selected', this.selectedVideo)
        this.$refs.libraryModal.hide()
      },
      resetVideo() {
        this.selectedVideo = null
        this.$emit('file-selected', null)
      },
    },
  }
</script>

<style>
  .video-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 5px;
  }

  .video-container.empty {
    background-color: #f8f9fa;
  }

  .video-name-banner {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .gallery__video-name-banner {
    padding: 5px 10px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .placeholder {
    color: #6c757d;
    border: 2px dashed #6c757d;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
  }
</style>

<style scoped>
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: nowrap;
  }
  .pagination-total {
    flex: 1;
    text-align: left;
    min-width: 120px;
  }
  .pagination-center {
    flex: 2;
    display: flex;
    justify-content: center;
    min-width: 200px;
  }
  .pagination-select {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    min-width: 160px;
  }
  @media (max-width: 768px) {
    .pagination-bar {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .pagination-total,
    .pagination-center,
    .pagination-select {
      text-align: center;
      justify-content: center !important;
    }
  }
</style>
